/**
 * Luna Sol — eigen winkelmandje (Upcart-stijl).
 * Werkt samen met Horizon's <theme-drawer id="cart-drawer"> en <cart-drawer-component>.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const SECTION_ID = 'cart-drawer-section';
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

const getRoots = () => Array.from(document.querySelectorAll('[data-luna-cart]'));
// De zichtbare instantie (open dialog) heeft voorrang; anders de eerste.
const getRoot = () =>
  getRoots().find((el) => el.closest('dialog')?.open) || getRoots()[0] || null;
const getDrawer = () => document.querySelector('theme-drawer#cart-drawer');

/* ---------- Geld ---------- */
function formatMoney(cents) {
  const format = getRoot()?.dataset.moneyFormat || '€{{amount_with_comma_separator}}';
  const value = Number(cents) || 0;
  const fmt = (n, decimals, thousands, decimal) => {
    const fixed = (n / 100).toFixed(decimals);
    const [whole, frac] = fixed.split('.');
    const withSep = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac ? withSep + decimal + frac : withSep;
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals': return fmt(value, 0, ',', '.');
      case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
      case 'amount_with_space_separator': return fmt(value, 2, ' ', ',');
      default: return fmt(value, 2, ',', '.');
    }
  });
}

const escapeHtml = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

function sizedImage(url, width) {
  if (!url) return '';
  let src = typeof url === 'string' ? url : url.src || '';
  if (!src) return '';
  if (src.startsWith('//')) src = 'https:' + src;
  if (/[?&]width=/.test(src)) return src;
  return src + (src.includes('?') ? '&' : '?') + 'width=' + width;
}

/* ---------- Sectie verversen ---------- */
async function fetchSection() {
  const res = await fetch(`${ROOT}?sections=${SECTION_ID}&_=${Date.now()}`, {
    credentials: 'same-origin',
    cache: 'no-store',
  });
  const json = await res.json();
  return json[SECTION_ID];
}

function applySection(html) {
  if (!html) return false;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const next = doc.querySelector('[data-luna-cart]');
  const roots = getRoots();
  if (!next || !roots.length) return false;
  roots.forEach((current) => {
    current.className = next.className;
    current.innerHTML = next.innerHTML;
  });
  initUpsells();
  return true;
}

async function refresh(sectionsHtml) {
  try {
    if (sectionsHtml && applySection(sectionsHtml)) return;
    applySection(await fetchSection());
  } catch (error) {
    console.warn('[luna-cart] verversen mislukt', error);
  }
}

/* ---------- Cart-acties ---------- */
let queue = Promise.resolve();

// Verzoeken worden na elkaar uitgevoerd, zodat snelle klikken niet verloren gaan.
function cartRequest(...args) {
  const run = queue.then(() => doCartRequest(...args));
  queue = run.catch(() => {});
  return run;
}

async function doCartRequest(url, payload, action, lines) {
  const root = getRoot();
  getRoots().forEach((el) => el.classList.add('is-busy'));

  let deferred = null;
  try {
    deferred = CartLinesUpdateEvent.createPromise();
    root?.dispatchEvent(
      new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise })
    );
  } catch (error) {
    deferred = null;
  }

  try {
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...payload, sections: SECTION_ID, sections_url: window.location.pathname }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Error');

    let cart = data;
    if (!('item_count' in data)) {
      cart = await (await fetch(`${ROOT}cart.js`, { credentials: 'same-origin' })).json();
    }

    if (deferred) {
      let cartForEvent;
      try {
        cartForEvent = CartLinesUpdateEvent.createCartFromAjaxResponse(cart);
      } catch (_) {
        cartForEvent = { totalQuantity: cart.item_count };
      }
      deferred.resolve({
        cart: cartForEvent,
        detail: {
          sections: data.sections,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'luna-cart',
          didError: false,
        },
      });
    }

    await refresh(data.sections && data.sections[SECTION_ID]);
    return true;
  } catch (error) {
    deferred?.reject(error);
    return error;
  } finally {
    getRoots().forEach((el) => el.classList.remove('is-busy'));
  }
}

async function changeLine(key, quantity) {
  if (quantity === 0) {
    getRoots().forEach((root) => {
      root.querySelector(`.luna-cart__item[data-key="${CSS.escape(key)}"]`)?.classList.add('is-removing');
    });
  }
  const result = await cartRequest(
    `${ROOT}cart/change.js`,
    { id: key, quantity },
    quantity > 0 ? 'update' : 'remove',
    [{ id: key, quantity }]
  );
  if (result instanceof Error) {
    const err = getRoot()?.querySelector(`[data-luna-error="${CSS.escape(key)}"]`);
    if (err) {
      err.textContent = result.message;
      err.hidden = false;
    }
    refresh();
  }
}

async function addVariant(variantId, button) {
  if (button) {
    button.disabled = true;
    button.textContent = 'Añadiendo…';
  }
  const result = await cartRequest(
    `${ROOT}cart/add.js`,
    { items: [{ id: Number(variantId), quantity: 1 }] },
    'add',
    [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity: 1 }]
  );
  if (result instanceof Error && button && button.isConnected) {
    button.disabled = false;
    button.textContent = 'Agotado';
  }
}

/* ---------- Upsells ---------- */
const recCache = new Map();
let slideIndex = 0;

async function getRecommendations(productId) {
  if (!productId) return [];
  if (recCache.has(productId)) return recCache.get(productId);
  let products = [];
  try {
    const res = await fetch(`${ROOT}recommendations/products.json?product_id=${productId}&limit=10&intent=related`);
    if (res.ok) products = (await res.json()).products || [];
  } catch (_) {
    products = [];
  }
  recCache.set(productId, products);
  return products;
}

function variantPrice(v) {
  return Number(v.price) || 0;
}

function renderCard(product) {
  const variants = (product.variants || []).filter((v) => v.available);
  if (!variants.length) return '';
  const first = variants[0];
  const img = (first.featured_image && first.featured_image.src) || product.featured_image;
  const compare = Number(first.compare_at_price) || 0;
  const onlyDefault = variants.length === 1 && /^default title$/i.test(first.title);
  const options = variants
    .map((v) => `<option value="${v.id}">${escapeHtml(v.title)}</option>`)
    .join('');

  return `
    <div class="luna-cart__card" data-luna-card data-variants='${escapeHtml(
      JSON.stringify(
        variants.map((v) => ({
          id: v.id,
          price: variantPrice(v),
          compare: Number(v.compare_at_price) || 0,
          image: (v.featured_image && v.featured_image.src) || '',
        }))
      )
    )}'>
      <a href="${escapeHtml(product.url)}" class="luna-cart__card-media">
        ${img ? `<img src="${escapeHtml(sizedImage(img, 200))}" alt="${escapeHtml(product.title)}" loading="lazy" data-luna-card-img>` : ''}
      </a>
      <div class="luna-cart__card-info">
        <a href="${escapeHtml(product.url)}" class="luna-cart__card-title">${escapeHtml(product.title)}</a>
        <p class="luna-cart__card-price" data-luna-card-price>
          ${compare > variantPrice(first) ? `<s class="luna-cart__compare">${formatMoney(compare)}</s>` : ''}
          <span class="luna-cart__price">${formatMoney(variantPrice(first))}</span>
        </p>
        <div class="luna-cart__card-actions">
          <select class="luna-cart__select" data-luna-card-select aria-label="Variante"${onlyDefault ? ' hidden' : ''}>${options}</select>
          <button type="button" class="luna-cart__add" data-luna-add="${first.id}">Añadir</button>
        </div>
      </div>
    </div>`;
}

function updateSlide(box) {
  const track = box.querySelector('[data-luna-track]');
  const total = track ? track.children.length : 0;
  if (!track || !total) return;
  slideIndex = ((slideIndex % total) + total) % total;
  track.style.transform = `translateX(-${slideIndex * 100}%)`;
  box.classList.toggle('is-single', total < 2);
}

async function initUpsells() {
  const box = getRoot()?.querySelector('[data-luna-upsell]');
  if (!box) return;
  const inCart = (box.dataset.inCart || '').split(',').filter(Boolean);
  const productId = box.dataset.productId;

  let products = await getRecommendations(productId);
  products = products.filter((p) => !inCart.includes(String(p.id)) && p.available !== false);

  if (!products.length) {
    try {
      const fallback = JSON.parse(getRoot()?.querySelector('[data-luna-fallback]')?.textContent || '[]');
      products = fallback.filter((p) => !inCart.includes(String(p.id)));
    } catch (_) {
      products = [];
    }
  }

  // Box kan inmiddels vervangen zijn door een nieuwere render
  if (!box.isConnected) return;

  const html = products.slice(0, 6).map(renderCard).filter(Boolean).join('');
  const track = box.querySelector('[data-luna-track]');
  if (!html || !track) {
    box.hidden = true;
    return;
  }
  track.innerHTML = html;
  box.hidden = false;
  updateSlide(box);
}

/* ---------- Event delegation ---------- */
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target || !target.closest('#cart-drawer')) return;

  const close = target.closest('[data-luna-close]');
  if (close) {
    event.preventDefault();
    getDrawer()?.close?.();
    return;
  }

  const qtyBtn = target.closest('[data-luna-qty]');
  if (qtyBtn) {
    const key = qtyBtn.dataset.lunaQty;
    const scope = qtyBtn.closest('[data-luna-cart]') || getRoot();
    const input = scope?.querySelector(`[data-luna-qty-input="${CSS.escape(key)}"]`);
    const current = parseInt(input?.value || '0', 10);
    const next = Math.max(0, current + parseInt(qtyBtn.dataset.step || '0', 10));
    if (input) input.value = String(next);
    changeLine(key, next);
    return;
  }

  const remove = target.closest('[data-luna-remove]');
  if (remove) {
    changeLine(remove.dataset.lunaRemove, 0);
    return;
  }

  const slide = target.closest('[data-luna-slide]');
  if (slide) {
    slideIndex += parseInt(slide.dataset.lunaSlide || '0', 10);
    updateSlide(slide.closest('[data-luna-upsell]'));
    return;
  }

  const add = target.closest('[data-luna-add]');
  if (add) {
    addVariant(add.dataset.lunaAdd, add);
  }
});

document.addEventListener('change', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target || !target.closest('#cart-drawer')) return;

  if (target.matches('[data-luna-qty-input]')) {
    const qty = Math.max(0, parseInt(target.value || '0', 10) || 0);
    changeLine(target.dataset.lunaQtyInput, qty);
    return;
  }

  if (target.matches('[data-luna-card-select]')) {
    const card = target.closest('[data-luna-card]');
    if (!card) return;
    let variants = [];
    try {
      variants = JSON.parse(card.dataset.variants || '[]');
    } catch (_) {}
    const v = variants.find((x) => String(x.id) === target.value);
    if (!v) return;
    card.querySelector('[data-luna-add]').dataset.lunaAdd = String(v.id);
    card.querySelector('[data-luna-card-price]').innerHTML =
      (v.compare > v.price ? `<s class="luna-cart__compare">${formatMoney(v.compare)}</s> ` : '') +
      `<span class="luna-cart__price">${formatMoney(v.price)}</span>`;
    const img = card.querySelector('[data-luna-card-img]');
    if (img && v.image) img.src = sizedImage(v.image, 200);
  }
});

/* ---------- Externe updates (productpagina, quick add, apps) ---------- */
function onExternalUpdate(event) {
  if (getRoots().includes(event.target)) return;
  event.promise
    ?.then(({ detail } = {}) => refresh(detail && detail.sections && detail.sections[SECTION_ID]))
    .catch((error) => {
      if (error?.name !== 'AbortError') refresh();
    });
  if (!event.promise) refresh();
}

document.addEventListener(StandardEvents.cartLinesUpdate, onExternalUpdate);
if (StandardEvents.cartDiscountUpdate) document.addEventListener(StandardEvents.cartDiscountUpdate, onExternalUpdate);

window.addEventListener('pageshow', (event) => {
  if (event.persisted) refresh();
});

initUpsells();
